/* ==========================================================================
   wg-wishlist-klaviyo.js  —  Wade Greens
   --------------------------------------------------------------------------
   WHAT THIS DOES
   1. Requires a logged-in customer before anything can be saved.
   2. Keeps each customer's saved list separate on a shared browser.
   3. Mirrors the saved list onto that customer's Klaviyo profile so
      price-drop / low-stock campaigns can be segmented against it.

   WHY IT HOOKS THE THEME INSTEAD OF REPLACING IT
   Halo's wishlist lives in assets/theme.js (186 KB) and writes a JSON array
   of product handles to localStorage under the key "wishlistItem". That file
   is far too large to rewrite through the API in one call, and we don't need
   to: every heart in the theme -- product cards AND product pages -- is
   [data-wishlist] carrying data-wishlist-handle and data-product-id.
   (Verified on /collections/succulents and on a product page.)
   So we let Halo keep owning the UI, and we listen around it.

   WHAT IT CANNOT DO
   Klaviyo's client-side API is WRITE-ONLY -- the public key cannot read a
   profile back, by design, or anyone could harvest the customer list. So the
   customer's own copy of their list still lives in this browser. A customer
   who saves on their phone and logs in on a laptop sees an empty list.
   Devyn gets the data either way; the customer just doesn't see it twice.

   MIGRATION NOTE
   This is deliberately a SINGLE list, because Klaviyo Customer Hub's
   Favorites is a single list. When Customer Hub goes live, its favorites
   take over the UI and this file gets deleted -- the profile properties
   written here already match what Hub segments on.

   TO REMOVE: delete this file and the two lines in layout/theme.liquid
   that render window.wgCustomer and this script tag.
   ========================================================================== */

(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'Tm93rx';           // public site ID, safe in the browser
  var KLAVIYO_REVISION   = '2025-07-15';
  var HALO_KEY           = 'wishlistItem';      // the key Halo's theme.js reads
  var SETTLE_MS          = 250;                 // let Halo's own handler write first

  var customer = window.wgCustomer || null;     // rendered by theme.liquid, logged-in only
  var productCache = {};

  /* ---------- small helpers ------------------------------------------- */

  function readList(key) {
    try {
      var raw = localStorage.getItem(key);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function writeList(key, arr) {
    try { localStorage.setItem(key, JSON.stringify(arr)); } catch (e) {}
  }

  function customerKey() {
    return customer && customer.id ? 'wg_wl_' + customer.id : null;
  }

  /* ---------- 1. account gate ------------------------------------------
     Capture phase so we run BEFORE Halo's own click handler. If nobody is
     logged in we stop the event dead and send them to sign in, returning
     them to the page they were on.                                       */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist]');
    if (!btn) return;
    if (customer && customer.email) return;     // logged in: let Halo handle it

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    window.location.href = '/account/login?return_url=' +
      encodeURIComponent(window.location.pathname + window.location.search);
  }, true);

  /* ---------- 2. per-customer separation -------------------------------
     Two people on one laptop must not see each other's list. Halo only ever
     reads "wishlistItem", so on load we swap THIS customer's list into that
     key, and clear it entirely when nobody is signed in.                  */

  function adoptCustomerList() {
    var key = customerKey();
    if (!key) { writeList(HALO_KEY, []); return; }

    var mine = readList(key);
    var visible = readList(HALO_KEY);

    // First login on this browser: inherit whatever was already hearted so
    // nobody loses saves made before the gate went in.
    if (!mine.length && visible.length) {
      mine = visible.slice();
      writeList(key, mine);
    }
    writeList(HALO_KEY, mine);
  }

  function persistCustomerList(list) {
    var key = customerKey();
    if (key) writeList(key, list);
  }

  /* ---------- 3. product details for the event payload ------------------
     One small fetch per product, cached. Using Shopify's own .js endpoint
     keeps title/price/image correct on every template, including quick view. */

  function getProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return null;
        var data = {
          ProductID:   p.id,
          ProductName: p.title,
          Handle:      p.handle,
          URL:         window.location.origin + '/products/' + p.handle,
          ImageURL:    p.featured_image ? ('https:' + p.featured_image) : '',
          Price:       (p.price || 0) / 100,
          Vendor:      p.vendor || '',
          Categories:  p.type ? [p.type] : []
        };
        productCache[handle] = data;
        return data;
      })
      .catch(function () { return null; });
  }

  /* ---------- 4. write to Klaviyo --------------------------------------
     Profile properties go through the documented client endpoint, which is
     built for exactly this: public key only, called from the browser.
     Events go through the onsite SDK that's already loaded on every page.  */

  function mirrorProfile(list) {
    if (!customer || !customer.email) return;

    return Promise.all(list.map(getProduct)).then(function (items) {
      var found = items.filter(Boolean);
      var body = {
        data: {
          type: 'profile',
          attributes: {
            email: customer.email,
            properties: {
              'Wishlist Items':       list,
              'Wishlist Product IDs': found.map(function (i) { return i.ProductID; }),
              'Wishlist Titles':      found.map(function (i) { return i.ProductName; }),
              'Wishlist Count':       list.length,
              'Wishlist Updated At':  new Date().toISOString()
            }
          }
        }
      };

      return fetch('https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(body)
      }).catch(function () { /* never let tracking break the page */ });
    });
  }

  function trackEvent(name, handle) {
    if (!customer || !customer.email) return;
    getProduct(handle).then(function (p) {
      if (!p) return;
      var klaviyo = window.klaviyo = window.klaviyo || [];
      klaviyo.push(['track', name, p]);
    });
  }

  function identify() {
    if (!customer || !customer.email) return;
    var klaviyo = window.klaviyo = window.klaviyo || [];
    klaviyo.push(['identify', {
      $email:      customer.email,
      $first_name: customer.first_name || '',
      $last_name:  customer.last_name || ''
    }]);
  }

  /* ---------- 5. watch for changes -------------------------------------- */

  var snapshot = [];

  function syncAfterClick() {
    var now = readList(HALO_KEY);
    var added   = now.filter(function (h) { return snapshot.indexOf(h) === -1; });
    var removed = snapshot.filter(function (h) { return now.indexOf(h) === -1; });

    if (!added.length && !removed.length) return;

    added.forEach(function (h)   { trackEvent('Saved to Wishlist', h); });
    removed.forEach(function (h) { trackEvent('Removed from Wishlist', h); });

    persistCustomerList(now);
    mirrorProfile(now);
    snapshot = now.slice();
  }

  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('[data-wishlist]'))) return;
    if (!customer || !customer.email) return;
    setTimeout(syncAfterClick, SETTLE_MS);
  });

  /* ---------- 6. boot ---------------------------------------------------- */

  function init() {
    adoptCustomerList();
    snapshot = readList(HALO_KEY);
    identify();
    // Push the list on every load too, so a list built before this shipped
    // (or before they had an account) lands on the profile at next sign-in.
    if (snapshot.length) mirrorProfile(snapshot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.wgWishlist = { sync: syncAfterClick, list: function () { return readList(HALO_KEY); } };
})();
